import { useCallback, useEffect, useMemo, useState } from 'react';
import { localFormat } from 'src/helpers/date-helpers';
import { IAggregatedValue, IDeviceBindingDto, QualityMetric } from 'src/services/api/api-client';
import { DateRangeEnum } from './useTrendModal.t';
import {
  getFilledInterval,
  getIntervalFromDate,
  changeRangeFunc,
  getBarAppearance,
  getBarType,
  isInStudyInterval,
} from './helpers';
import { BarData } from 'uikit/appBarChart/AppBarChart.component';

export const useGetBarData = (
  data: IAggregatedValue[],
  interval: Interval,
  dateRange: DateRangeEnum,
  deviceBindings: IDeviceBindingDto[] | undefined,
  studyStartedAt: Date | null,
  studyFinishedAt: Date | null,
  valueMapFunc?: (value: number | null | undefined) => number,
) => {
  const params = getFilledInterval(dateRange);
  const getChanges = useCallback(
    (prevSource: IAggregatedValue | undefined | null, curSource: IAggregatedValue | undefined | null) => {
      if (!prevSource || !curSource) return null;
      return (curSource.value ?? 0) - (prevSource.value ?? 0);
    },
    [],
  );

  return useMemo(
    () =>
      params.getPointFunc(interval).map((pointDate) => {
        const prevSource = params.findValueFunc(data, pointDate, 1);
        const source =
          params.findValueFunc(data, pointDate, 0) ??
          (isInStudyInterval(pointDate, studyStartedAt, studyFinishedAt)
            ? ({
                from: pointDate,
                until: pointDate,
                qualityMetric: QualityMetric.Empty,
                value: null,
              } satisfies IAggregatedValue)
            : null);
        const barType = getBarType(pointDate, source, deviceBindings);

        return {
          key: localFormat(pointDate, params.format),
          value: valueMapFunc?.(source?.value) ?? source?.value ?? 0,
          appearance: getBarAppearance(barType, source?.qualityMetric),
          source: source,
          changes: getChanges(prevSource, source),
        } satisfies BarData<IAggregatedValue | null>;
      }),
    [data, deviceBindings, getChanges, interval, params, studyFinishedAt, studyStartedAt, valueMapFunc],
  );
};

export const useGetInterval = (dateRange: DateRangeEnum, lastTherapyDate: Date | null) => {
  const [interval, setInterval] = useState<Interval>(getIntervalFromDate(lastTherapyDate ?? new Date(), dateRange));

  // This effect sets default interval when dateRange changes
  useEffect(() => {
    setInterval(getIntervalFromDate(lastTherapyDate ?? new Date(), dateRange));
  }, [dateRange, lastTherapyDate]);

  return useMemo(
    () => ({
      interval: interval,
      goPrev: () => {
        setInterval((x) => getIntervalFromDate(changeRangeFunc(dateRange, 'prev')(x.start, 1), dateRange));
      },
      goNext: () => {
        setInterval((x) => getIntervalFromDate(changeRangeFunc(dateRange, 'next')(x.start, 1), dateRange));
      },
    }),
    [dateRange, interval],
  );
};
